import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCompany } from "@/context/CompanyContext";
import { useAuth } from "@/context/AuthContext";
import { Search, History, Star } from "lucide-react";
import { toast } from "sonner";

const norm = (s) => String(s || "").toLowerCase().replace(/^rp-?#?/, "").replace(/^#/, "").trim();
const recentKey = (uid) => `rp_recent_clients_${uid}`;
export const readRecent = (uid) => { try { return JSON.parse(localStorage.getItem(recentKey(uid)) || "[]"); } catch { return []; } };
const pinKey = (uid) => `rp_pinned_clients_${uid}`;
export const readPinned = (uid) => { try { return JSON.parse(localStorage.getItem(pinKey(uid)) || "[]"); } catch { return []; } };
export const togglePinned = (uid, id) => { const cur = readPinned(uid); const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]; localStorage.setItem(pinKey(uid), JSON.stringify(next)); return next; };
export const pushRecent = (uid, id) => { const next = [id, ...readRecent(uid).filter((x) => x !== id)].slice(0, 5); localStorage.setItem(recentKey(uid), JSON.stringify(next)); return next; };

const Hit = ({ c, onPick, testid, pinned, onPin }) => (
  <div className="flex items-center gap-1 rounded hover:bg-accent group">
    <button type="button" data-testid={testid} onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(c)} className="flex-1 min-w-0 flex items-center justify-between gap-2 px-2 py-1.5 text-sm text-left">
      <span className="truncate">{c.logo_url ? <img src={c.logo_url} alt="" className="inline w-4 h-4 object-contain mr-1.5 align-middle" /> : <span className="mr-1.5">{c.logo}</span>}{c.name}</span>
      {c.client_id && <span className="text-[10px] font-mono-num px-1.5 py-0.5 rounded bg-muted text-muted-foreground">#{c.client_id}</span>}
    </button>
    <button type="button" data-testid={`client-pin-${c.id}`} title={pinned ? "Unpin" : "Pin to top"} onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); onPin(c.id); }} className={`p-1.5 mr-0.5 rounded ${pinned ? "text-amber-500" : "text-muted-foreground/40 group-hover:text-muted-foreground hover:!text-amber-500"}`}>
      <Star className={`w-3.5 h-3.5 ${pinned ? "fill-current" : ""}`} />
    </button>
  </div>
);

export function ClientSearch() {
  const { companies, activeCompanyId, selectCompany } = useCompany();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [recentIds, setRecentIds] = useState(() => readRecent(user.id));
  const [pinnedIds, setPinnedIds] = useState(() => readPinned(user.id));
  const pin = (id) => { const next = togglePinned(user.id, id); setPinnedIds(next); ref.current?.focus(); setOpen(true); };
  const ref = useRef();
  useEffect(() => {
    const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); ref.current?.focus(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => { if (activeCompanyId) setRecentIds(pushRecent(user.id, activeCompanyId)); }, [activeCompanyId, user.id]);
  const nq = norm(q);
  const isPinned = (id) => pinnedIds.includes(id);
  const matches = nq ? companies.filter((c) => norm(c.client_id).startsWith(nq) || norm(c.client_id) === nq || (c.name || "").toLowerCase().includes(q.toLowerCase())).sort((a, b) => isPinned(b.id) - isPinned(a.id)).slice(0, 6) : [];
  const pinned = pinnedIds.map((id) => companies.find((c) => c.id === id)).filter(Boolean);
  const recent = recentIds.map((id) => companies.find((c) => c.id === id)).filter((c) => c && c.id !== activeCompanyId && !isPinned(c.id)).slice(0, 5);
  const jump = (c) => { selectCompany(c.id); setQ(""); setOpen(false); ref.current?.blur(); toast.success(`Switched to ${c.name}${c.client_id ? ` · #${c.client_id}` : ""}`); navigate("/dashboard"); };
  const submit = (e) => {
    e.preventDefault();
    const exact = companies.find((c) => norm(c.client_id) === nq) || (matches.length === 1 ? matches[0] : null);
    if (exact) jump(exact); else if (!matches.length && nq) toast.error(`No client matches "${q}"`);
  };
  const showRecent = open && !nq && recent.length > 0;
  const showPinned = open && !nq && pinned.length > 0;
  return (
    <div className="relative hidden md:block" data-testid="client-search">
      <form onSubmit={submit}>
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input ref={ref} data-testid="client-search-input" value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Client # or name  ⌘K" className="h-9 w-[170px] lg:w-[210px] rounded-md border border-input bg-background pl-8 pr-2 text-xs font-mono-num focus:outline-none focus:ring-2 focus:ring-ring" />
      </form>
      {(showRecent || showPinned || (open && nq)) && (
        <div className="absolute z-50 mt-1 w-[300px] rounded-md border border-border bg-popover shadow-lg p-1" data-testid="client-search-results">
          {showPinned && <>
            <div className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1" data-testid="client-search-pinned-label"><Star className="w-3 h-3 fill-current text-amber-500" /> Pinned</div>
            {pinned.map((c) => <Hit key={c.id} c={c} onPick={jump} pinned onPin={pin} testid={`client-search-pinned-${c.id}`} />)}
          </>}
          {showRecent && <>
            <div className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1" data-testid="client-search-recent-label"><History className="w-3 h-3" /> Recent clients</div>
            {recent.map((c) => <Hit key={c.id} c={c} onPick={jump} pinned={false} onPin={pin} testid={`client-search-recent-${c.id}`} />)}
          </>}
          {open && nq && (matches.length === 0 ? <div className="px-2 py-2 text-xs text-muted-foreground" data-testid="client-search-empty">No client matches "{q}"</div> : matches.map((c) => <Hit key={c.id} c={c} onPick={jump} pinned={isPinned(c.id)} onPin={pin} testid={`client-search-hit-${c.id}`} />))}
        </div>
      )}
    </div>
  );
}
